<!--
This component handle report/export downloading.
Show 'processing...' if need, or show `file will be send by email`.
Initially, it was a part of ReportStore/actions.js, but was extracted to a separate component.

In the future, if custom UI will be need, we can add option to hide this html and send events to parent component.

NOTE: This component can be deprecated when background_tasks_web_sockets is enabled to all orgs.
The new component is called AppExporterWebsockets.
-->
<template>
  <AppAlert
    v-if="queued"
    type="warning"
    :class="alertClass"
    :message="message"
    icon-classes="hi hi-info"
  />
</template>

<script>
import { BackgroundTasksResource } from '~/resources';
import { mapGetters } from 'vuex';

export default {
  props: {
    delayedMessage: {
      type: String,
      default: 'Your export is being prepared and will start downloading shortly.',
    },
    backgroundedMessage: {
      type: String,
      default:
        'Your export is being prepared and will start downloading shortly. If you exit this page, the export will be emailed to you.',
    },
    alertClass: {
      type: String,
      default: undefined,
    },
  },
  data: () => ({
    queue: {},
  }),
  computed: {
    ...mapGetters(['organizationId']),
    queued() {
      return !!this.queue.queueId;
    },
    message() {
      return this.queue.type === 'delayed' ? this.delayedMessage : this.backgroundedMessage;
    },
  },
  methods: {
    // Use ID to not clear current info if multiple exports are in progress
    clearQueued(queueId) {
      if (this.queue.queueId === queueId) {
        this.queue = {};
      }
    },
    download(format, { background = false, url = document.location } = {}) {
      return this.waitAndDownload(format, new URL(url, document.location), { background });
    },
    async waitAndDownload(format, url, { background }) {
      // Downloading reports can work in three different ways:
      //
      // 1. If the file is received in less than 2 seconds, we'll download it normally.
      // 2. If the file takes more than 2 seconds to be available, we display an info alert explaining why.
      //    This alert is dismissed after the 'Save as' prompt appears. (on BE we process in foreground)
      // 3. If the file is too large to be immediately available, a same info alert is displayed,
      //    but on BE we process it in background, on FE check status every 10 seconds. When the file is ready, we download it.

      this.clearQueued(this.queueId);
      const queueId = Date.now();

      url.searchParams.set('format', format);
      background && url.searchParams.set('background', background);

      try {
        // If more than 2 seconds pass after we request the download, the queueId
        // will be set, which triggers the UI to show an alert explaing that things
        // are taking some time to be ready.
        const delayedCheck = setTimeout(() => {
          this.queue = { queueId, type: 'delayed' };
        }, 2001);
        let response = await fetch(url);

        if (response?.status === 500 && !background) {
          // if timeout, retry with force background
          url.searchParams.set('background', 'true');
          response = await fetch(url);
        }

        if (response) {
          clearTimeout(delayedCheck);

          const type = response.headers.get('content-type');

          // If the response is JSON, this means that the download will be processed in the
          // background queue. The queue type is set to 'backgrounded' and the UI shows a dialog explaining
          // this.
          if (type.includes('application/json')) {
            const json = await response.json();

            if (json.success && json.background_id) {
              this.queue = { queueId, type: 'backgrounded' };
              await this.waitBackgrounded(json.background_id, queueId);
            } else {
              this.$appMessage(json.alert || 'Unable to generate export file', { sticky: false });
              this.clearQueued(queueId);
            }
          } else {
            // If the response is not JSON, this means we have the file ready for download.
            await this.downloadResponse(response, { format });
            this.clearQueued(queueId);
          }
        }
      } catch (err) {
        this.showError(queueId, false);
        throw new Error('Unable to generate report file for download', err);
      }
    },
    async downloadResponse(response, { format }) {
      // We force that download by attaching a link and clicking on it.
      const type = format === 'csv' ? 'text/csv' : 'application/pdf';
      const blob = new Blob([await response.blob()], { type });
      const object = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const parts = response.headers.get('content-disposition').split(';');

      link.href = object;
      link.download = parts[1].split('=')[1].split('"').join('');
      link.click();

      // Clear object URL from memory after 8s
      setTimeout(() => URL.revokeObjectURL(link.href), 8000);
    },

    async waitBackgrounded(backgroundId, queueId) {
      const intervalId = setInterval(async () => {
        const { success, ready, url, filename } = await BackgroundTasksResource.get({
          organizationId: this.organizationId,
          id: backgroundId,
        });

        if (!success) {
          clearInterval(intervalId);
          this.showError(queueId);
        } else if (ready) {
          clearInterval(intervalId);
          this.clearQueued(queueId);
          this.downloadUrl(url);
          this.$appMessage(`${filename} is downloaded`, { sticky: true });
        }
      }, 10000);
    },
    downloadUrl(url) {
      if (url) {
        const link = document.createElement('a');

        link.href = url;
        link.click();
      }
    },
    showError(queueId, sticky = true) {
      this.$appMessage('Unable to generate export file for download', { sticky });
      this.clearQueued(queueId);
    },
  }, // end methods
};
</script>
